import { Trans, useTranslation } from 'react-i18next'
import { useCallback } from 'react'

import { ActionModal, Modal } from '@percent/lemonade'
import { useMutation } from '@percent/workplace-giving/common/hooks'
import { unpublishCampaign } from '@percent/workplace-giving/api/campaigns/unpublishCampaign/unpublishCampaign'
import { ErrorResponse, okResponse } from '@percent/workplace-giving/api/goodstack'
import type { UseModalReturn } from '@percent/domain/giving'

export type UnpublishCampaignModalProps = Pick<UseModalReturn, 'isOpen' | 'closeModal'> &
  Readonly<{
    givingCampaign: {
      id: string
      title: string
    } | null
    onSuccess: () => void
    onError: (error?: ErrorResponse | unknown) => void
  }>

export function UnpublishCampaignModal({
  isOpen,
  closeModal,
  givingCampaign,
  onSuccess,
  onError
}: UnpublishCampaignModalProps) {
  const { t } = useTranslation()

  const { isPending, mutate } = useMutation({ mutationFn: unpublishCampaign })

  const handleUnpublishCampaign = useCallback(() => {
    if (isPending || !givingCampaign?.id) return

    mutate([givingCampaign.id], {
      onSuccess: res => {
        if (!okResponse(res)) {
          onError(res)

          return
        }

        onSuccess()
      },
      onError
    })
  }, [givingCampaign?.id, mutate, onSuccess, onError, isPending])

  return (
    <Modal open={isOpen} onClose={closeModal}>
      <ActionModal
        title={t('workplace_giving.myCampaigns.actions.unpublishConfirmationModal.title')}
        primaryButtonText={t('workplace_giving.common.confirm')}
        secondaryButtonText={t('workplace_giving.common.cancel')}
        handleClose={closeModal}
        handleSubmit={handleUnpublishCampaign}
        loading={isPending}
      >
        <Trans
          i18nKey="workplace_giving.myCampaigns.actions.unpublishConfirmationModal.description"
          components={{ b: <b /> }}
          values={{ campaignName: givingCampaign?.title ?? '' }}
        />
      </ActionModal>
    </Modal>
  )
}
